import {
  createSearchParams,
  NavigateFunction,
  type To,
  URLSearchParamsInit
} from 'react-router-dom'
import {logger, Util, UUID} from 'yougile-util'
import {
  DataObjectId,
  googleCalendarRedirectPage,
  Hub,
  Path
} from 'yougile-model'
import {DepartmentsClient} from '@client/departments-client'
import {PermissionsChecker} from '@client/permissions-checker'
import {SharedBoardsClient} from '@client/shared-boards-client'
import {SharedBoardsHelper} from '@client/shared-boards-helper'
import {lastSeenBoardKey, Storage} from '@client/storage'
import {Sync} from '@client/sync'
import {SyncEvents} from '@client/sync-events'
import {Backend} from '@common/backend'
import {Debouncer} from '@common/debouncer'
import {setGoogleCalendarAuthCode} from '@common/google-calendar'
import {Tracking} from '@common/tracking'
import {TrackingEvent} from '@common/tracking-events'
import {BoardTabBg} from '@components/board/board-tab-bg'
import {StatisticsSubpage} from '@components/statistics/statistics-base'
import {tryToSendPerformanceEvent} from '@components/team/lazy-monitor/lazy-events'
import {EventBus} from '../client/event-bus'
import {beforeNavigateEvent} from '../client/events'
import {Company} from './company'
import {globals} from './globals-base'
import {idTasksApi} from './id-tasks/id-tasks-store'
import {reopenChat} from './id-tasks/reopen-chat'
import {KeyboardShortcuts} from './keyboard-shortcuts'
import {LoginFromUrl} from './login-from-url'
import {isPage, Page} from './page'
import {Project} from './project'
import {getProjectByTask} from './task'
import {ThemeUtils} from './theme-utils'

const trackingDebouncer = new Debouncer(2000)
const trackPageView = (): void => {
  trackingDebouncer.runNowButNotRightAfter(() => {
    void Tracking.send(TrackingEvent.pageNavigate)
    tryToSendPerformanceEvent()
  })
}

let prevURLProcessedByUp = ''
let prevPageProcessedByUp = ''

export class PageService {
  private static initialized = false
  private static currentProject?: Hub
  private static currentBoard?: Hub
  private static locationListeners: Map<UUID, () => void> = new Map()
  private static navigate: (to: To) => void = Util.noop
  private static searchUpdaterBeforeSetPath: (() => string) | undefined

  // see init() method
  static getUrl: () => string = Util.notInitialized
  static getPath: () => string = Util.notInitialized
  static getHash: () => string = Util.notInitialized
  static getSearch: () => URLSearchParams = Util.notInitialized
  static setUrl: (url: string) => void = Util.notInitialized
  static setPath: (path: string) => void = Util.notInitialized
  static setHash: (hash: string) => void = Util.notInitialized
  static setSearch: (search: URLSearchParamsInit) => void = Util.notInitialized
  //
  static getSubpage(depth = 2): string {
    const page = PageService.getPage()
    if (['statistics', 'projects'].includes(page)) {
      const path = PageService.getPath()
      const parts = path.split('/')
      if (parts.length >= depth + 1) {
        return parts[depth]
      } else {
        return ''
      }
    } else {
      return ''
    }
  }

  static hasEnteredSubdomain(): boolean {
    return !!sessionStorage.getItem('yg-enter-subdomain')
  }

  static enterSubdomain(): void {
    sessionStorage.setItem('yg-enter-subdomain', '1')
    location.reload()
  }

  static getCompanySubdomain(): string {
    const host = location.host
    let d = ''
    if (host.split('.').length === 3 && host.includes('yougile.com')) {
      d = location.host.split('.')[0]
    } else {
      d = (location.search.split('?').pop()?.split('domain=')[1] || '').split(
        '&'
      )[0]
    }
    if (Backend.usesProxy()) {
      return ''
    }
    if (
      d &&
      d.length >= 3 &&
      !['test', 'test-new', 'test-ru', 'test-en', 'www', 'preview'].includes(
        d
      ) &&
      !d.includes('staging')
    ) {
      return d
    } else {
      return ''
    }
  }

  static getSearchParam(name: string): string {
    const encoded = new URL(location.href).searchParams.get(name)
    return encoded ? decodeURIComponent(encoded) : ''
  }

  static getPage(): Page {
    const path = PageService.getPath()
    const pageName = path.split('/').slice(1, 2)[0] || ''
    const subdomain = PageService.getCompanySubdomain()

    if (subdomain && !PageService.hasEnteredSubdomain()) {
      return 'company-login'
    }
    if (globals.noLicense) {
      return 'no-license'
    }
    if (globals.noUsers) {
      return 'no-users'
    }
    if (
      isPage(pageName) &&
      (pageName === 'register' ||
        pageName === 'mail-settings' ||
        pageName === 'recovery' ||
        pageName === 'email-change')
    ) {
      return pageName
    }

    if (!globals.currentUserId) {
      return 'login'
    }

    const companyId = globals.getCompanyId()
    if (!companyId || !Sync.get({companyId})) {
      return 'loading'
    }
    if (SharedBoardsClient.getId()) {
      return 'board'
    }

    if (pageName === '') {
      return 'projects'
    }
    if (isPage(pageName)) {
      return pageName
    } else {
      return 'board'
    }
  }

  static goToExtensions(extensionId?: string): void {
    trackPageView()
    PageService.setPath(
      `/projects/extensions${extensionId ? `/${extensionId}` : ''}`
    )
  }

  static goToStatistics(subpage?: StatisticsSubpage): void {
    trackPageView()
    PageService.setPath(`/statistics${subpage ? `/${subpage}` : ''}`)
  }

  static goTo(pageType: Page, cb: () => void = Util.noop): void {
    trackPageView()
    switch (pageType) {
      case 'no-license':
        globals.noLicense = true
        break
      case 'no-users':
        globals.noUsers = true
        break
      case 'login':
        delete globals.currentUserId
        break
      case 'loading':
        globals.setCompanyId(undefined)
        break
      case 'board':
        PageService.setPath('/')
        break
      default:
        PageService.setPath('/' + pageType)
    }

    void PageService.up().then(() => {
      void BoardTabBg.refresh()
      void ThemeUtils.upTheme()
    })

    setTimeout(() => cb(), 10)
  }

  static async up(): Promise<void> {
    const url = PageService.getUrl()
    const page = PageService.getPage()

    if (prevURLProcessedByUp === url && page === prevPageProcessedByUp) {
      return
    }
    prevURLProcessedByUp = url
    prevPageProcessedByUp = page

    const newProject = PageService.getProjectFromUrl()
    let newBoard = PageService.getBoardFromUrl()
    if (!newProject && page === 'board') {
      const companyId = globals.getCompanyId()
      const projects = Project.listProjects(companyId, 'not-archive', true)
      if (projects.length === 0) {
        if (!SharedBoardsClient.getId()) {
          PageService.goTo('projects')
        }
        return
      }
      Project.fixOrderIfNeeded(companyId)

      const hash = PageService.getHash()
      const dataByHash = await getProjectByHash(hash)

      if (dataByHash) {
        PageService.goToProject(dataByHash.project.id, companyId)
      } else {
        const ord = Project.getOrder(companyId)
        const fixedOrd = ord.filter(
          id => id && !!projects.find(h => h?.id === id)
        )
        const firstProjectId = fixedOrd?.[0] ?? projects[0].id

        PageService.goToProject(firstProjectId, companyId)
      }
      return
    }

    if (newProject && !newBoard) {
      const boardsOrder = Project.getBoardsOrder(newProject)
      boardsOrder.cleanDeleted()
      const box = Sync.get(newProject)
      if (!box) {
        return
      }

      const lastSeenBoards: {[key: DataObjectId]: DataObjectId} =
        Storage.getItemAsJson(lastSeenBoardKey) ?? {}

      const lastSeenBoardId = lastSeenBoards[newProject.id]
      const lastSeenBoard =
        box && lastSeenBoardId && box.getHub(lastSeenBoardId)
      if (
        lastSeenBoard &&
        !lastSeenBoard.deleted &&
        lastSeenBoard.location === newProject.id
      ) {
        newBoard = lastSeenBoard
      } else {
        const calculatedHierarchy = DepartmentsClient.getHierarchy(box)

        const availableOrder = boardsOrder.getList().filter(e => {
          return (
            e instanceof Hub &&
            !e.deleted &&
            !e.title.startsWith('.') &&
            PermissionsChecker.checkShow('ignore-departments', e, {
              calculatedHierarchy
            })
          )
        })
        newBoard = availableOrder[0] as Hub
      }
    }

    const companyId = newProject?.companyId
    const globalsCompanyId = globals.getCompanyId()
    if (companyId && globalsCompanyId && companyId !== globalsCompanyId) {
      logger.log('setting company based on project url')
      Company.setCurrent(companyId, true)
    }

    PageService.currentProject = newProject
    PageService.currentBoard = newBoard
    ;[...PageService.locationListeners.values()].forEach(cb => {
      try {
        cb()
      } catch (e) {
        logger.error(e)
      }
    })

    if (page === googleCalendarRedirectPage) {
      const code = PageService.getSearch().get('code') || ''
      await setGoogleCalendarAuthCode(code)
      window.close()
    }

    reopenChat(100)
  }

  static init(): void {
    LoginFromUrl.init()
    KeyboardShortcuts.init()
    const getHashStr = (): string =>
      location.hash && !location.hash.startsWith('#')
        ? `#${location.hash}`
        : location.hash ?? ''

    PageService.getUrl = () => {
      const url = `${location.pathname}${location.search}${getHashStr()}`
      return url.startsWith('/team/') ? url.replace(/^\/team/, '') : url
    }
    PageService.getPath = () => {
      const path = location.pathname ?? ''
      return path.startsWith('/team') ? path.replace(/^\/team/, '') : path
    }
    PageService.getHash = () => location.hash || ''
    PageService.setUrl = url => {
      PageService.navigate(url.startsWith('/board/') ? url : '/team' + url)
    }
    PageService.setPath = path => {
      const search =
        PageService.searchUpdaterBeforeSetPath?.() ?? location.search
      const pathname = path.startsWith('/board/') ? path : `/team${path}`
      PageService.navigate({
        pathname,
        hash: getHashStr(),
        search
      })
    }
    PageService.setHash = hash => {
      const hashStr = hash && !hash.startsWith('#') ? `#${hash}` : hash
      PageService.navigate(`${location.pathname}${location.search}${hashStr}`)
    }
    PageService.setSearch = (search: URLSearchParamsInit) => {
      PageService.navigate({
        pathname: location.pathname,
        hash: getHashStr(),
        search: `?${createSearchParams(search)}`
      })
    }
    PageService.getSearch = () => {
      return createSearchParams(location.search)
    }

    void this.up()
    SyncEvents.addAnyListener(PageService.syncCurrentBoard)

    PageService.initialized = true
  }

  static navigationInit(navigate: NavigateFunction): void {
    PageService.navigate = async (to: To) => {
      const results = EventBus.dispatch(beforeNavigateEvent(to))
      for (const result of results) {
        if (await result) {
          return
        }
      }
      navigate(to)
    }
  }

  static setSearchUpdaterBeforeSetPath(cb?: () => string): void {
    PageService.searchUpdaterBeforeSetPath = cb
  }

  static removeLocationListener(id?: UUID): void {
    if (id) {
      PageService.locationListeners.delete(id)
    }
  }

  static addLocationListener(cb: () => void, firstRun = true): UUID {
    const id = Util.UUID()
    PageService.locationListeners.set(id, cb)
    if (PageService.initialized && firstRun) {
      cb()
    }
    return id
  }

  static getCurrentBoard(): Hub {
    return SharedBoardsHelper.getBoard() ?? PageService.currentBoard
  }

  static getCurrentProject(): Hub {
    return SharedBoardsHelper.getProject() ?? PageService.currentProject
  }

  static goToProject(projectId: DataObjectId, companyId: DataObjectId): void
  static goToProject(project: Hub): void
  static goToProject(
    projectOrProjectId: Hub | DataObjectId,
    companyId?: DataObjectId
  ): void {
    const project =
      projectOrProjectId instanceof Hub
        ? projectOrProjectId
        : (Sync.get({companyId}).get(projectOrProjectId) as Hub)

    const box = Sync.get(project)
    PageService.setPath(box.calculatePath(project))
    trackPageView()

    void PageService.up().then(() => {
      void BoardTabBg.refresh()
      void ThemeUtils.upTheme()
    })
  }

  static goToBoard(boardId: DataObjectId, companyId: DataObjectId): void
  static goToBoard(board: Hub): void
  static goToBoard(
    boardOrBoardId: Hub | DataObjectId,
    companyId?: DataObjectId
  ): void {
    const board =
      boardOrBoardId instanceof Hub
        ? boardOrBoardId
        : (Sync.get({companyId}).get(boardOrBoardId) as Hub)

    const box = Sync.get(board)
    PageService.setPath(box.calculatePath(board))

    const lastSeenBoards: {[key: DataObjectId]: DataObjectId} =
      Storage.getItemAsJson(lastSeenBoardKey) ?? {}
    lastSeenBoards[board.location] = board.id
    Storage.setItem(lastSeenBoardKey, JSON.stringify(lastSeenBoards))

    trackPageView()

    void PageService.up().then(() => {
      void BoardTabBg.refresh()
      void ThemeUtils.upTheme()
    })
  }

  private static getBoardFromUrl(): Hub | undefined {
    if (SharedBoardsClient.getId()) {
      return SharedBoardsHelper.getBoard()
    }
    if (!globals.currentUserId) {
      return undefined
    }
    const path = PageService.getPath()
    const pathDecoded = path ? (decodeURIComponent(path) as Path) : ''
    if (!pathDecoded || pathDecoded === '/') {
      return undefined
    } else {
      const s = Sync.findSandboxByPath(pathDecoded)
      if (!s) {
        return undefined
      }
      const prId = s.findIdByPath(pathDecoded, true)
      if (!prId) {
        return undefined
      }
      const res = s.get(prId) as Hub
      if (res.location !== res.companyId) {
        return res
      } else {
        return undefined
      }
    }
  }

  private static getProjectFromUrl(): Hub | undefined {
    if (SharedBoardsClient.getId()) {
      return SharedBoardsHelper.getProject()
    }
    if (!globals.currentUserId) {
      return undefined
    }
    const path = PageService.getPath()
    const pathDecoded = path ? (decodeURIComponent(path) as Path) : ''
    if (!pathDecoded || pathDecoded === '/') {
      return undefined
    } else {
      const s = Sync.findSandboxByPath(pathDecoded)
      if (!s) {
        return undefined
      }
      const prId = s.findIdByPath(pathDecoded, true)
      if (!prId) {
        return undefined
      }
      const res = s.get(prId) as Hub
      if (!res || res.id === res.companyId) {
        return undefined
      } else if (res.location === res.companyId) {
        return res
      } else {
        return s.get(res.location) as Hub
      }
    }
  }

  private static async syncCurrentBoard(): Promise<void> {
    if (!PageService.getCurrentBoard() && PageService.getBoardFromUrl()) {
      await PageService.up()
    }
  }
}

export const getProjectByHash = async (
  hash = ''
): Promise<{project: Hub; board: Hub} | undefined> => {
  if (hash.toLowerCase().includes('-')) {
    const [prefix, idPart] = hash.replace('#', '').toLowerCase().split('-')
    const idTask = idPart.includes(',') ? idPart.split(',')[0] : idPart
    if (!idTask) {
      return undefined
    }

    const taskId = await idTasksApi.getReverseIdTaskForce(
      idTask,
      prefix || 'ID'
    )
    if (!taskId) {
      return undefined
    }

    return getProjectByTask(taskId)
  } else if (hash.includes('chat:')) {
    const shortId = hash.split('chat:').pop()?.split(',').shift()
    if (!shortId) {
      return undefined
    }
    const task = Sync.getCurrentSandbox().findByShortId(shortId) as Hub
    if (!task) {
      return undefined
    }
    return getProjectByTask(task.id)
  }

  return undefined
}
